import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentLanguageCode } from '@/shared/constants';
import { Museum, Work } from '@/shared/types';
import phoneIcon from '@/assets/svg/phone_icon.svg';
import qrIcon from '@/assets/svg/qr_icon.svg';
import { WORK_SECTIONS, WORK_SECTION_PANEL_ID, workSectionTabId, type WorkSection } from '../hooks/useCreateWorkForm';
import { WorkSectionIcon } from './WorkSectionIcon';

interface CreateWorkSidebarProps {
  work?: Work | null;
  museum: Museum | null;
  isQrCodeEnabled: boolean;
  canUseQrCode: boolean;
  currentLanguage: ContentLanguageCode;
  statusByLanguage: Record<string, 'draft' | 'published'>;
  saving: boolean;
  activeSection: WorkSection;
  onSectionChange: (section: WorkSection) => void;
  onOpenQrModal: () => void;
  onPreview: () => void;
  canPreview: boolean;
  onSave: () => void;
  onRequestClose: () => void;
}

/**
 * Colonne de gauche (Figma admin 323:5421) : onglets de la modale épisode, puis
 * les actions. Onglets verticaux : flèches haut/bas, Début/Fin.
 */
export const CreateWorkSidebar: React.FC<CreateWorkSidebarProps> = ({
  work,
  museum,
  isQrCodeEnabled,
  canUseQrCode,
  currentLanguage,
  statusByLanguage,
  saving,
  activeSection,
  onSectionChange,
  onOpenQrModal,
  onPreview,
  canPreview,
  onSave,
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const tabRefs = useRef<Partial<Record<WorkSection, HTMLButtonElement | null>>>({});

  const focusSection = (section: WorkSection) => {
    onSectionChange(section);
    tabRefs.current[section]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = WORK_SECTIONS.length - 1;
    const target = {
      ArrowDown: index === last ? 0 : index + 1,
      ArrowUp: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[e.key];
    if (target === undefined) return;
    e.preventDefault();
    focusSection(WORK_SECTIONS[target]);
  };

  return (
    <div className="create-work-sidebar">
      <div className="create-work-nav" role="tablist" aria-orientation="vertical" aria-label={t('works.sections.label')}>
        {WORK_SECTIONS.map((section, index) => {
          const selected = section === activeSection;
          return (
            <button
              key={section}
              ref={(el) => {
                tabRefs.current[section] = el;
              }}
              type="button"
              role="tab"
              id={workSectionTabId(section)}
              aria-selected={selected}
              aria-controls={WORK_SECTION_PANEL_ID}
              tabIndex={selected ? 0 : -1}
              className={`create-work-nav__item${selected ? ' active' : ''}`}
              onClick={() => onSectionChange(section)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              <WorkSectionIcon section={section} />
              {t(`works.sections.${section}.title`)}
            </button>
          );
        })}
      </div>

      <div className="create-work-sidebar-actions">
        {work && (
          <>
            <button
              type="button"
              className="preview-btn"
              onClick={onPreview}
              disabled={!canPreview}
              title={!canPreview ? t('works.qrCode.pwaUrlMissing') : undefined}
            >
              <img src={phoneIcon} alt={t('exhibitions.preview')} className="phone-icon" />
              {t('exhibitions.preview')}
            </button>
            {isQrCodeEnabled && (
              <>
                <button
                  type="button"
                  className="qr-code-btn"
                  onClick={onOpenQrModal}
                  disabled={!canUseQrCode}
                  aria-label={t('works.qrCode.showButton')}
                  title={!museum?.pwaUrlBase ? t('works.qrCode.pwaUrlMissing') : undefined}
                >
                  <img src={qrIcon} alt="" className="qr-icon" aria-hidden />
                  {t('works.qrCode.showButton')}
                </button>
                {!museum?.pwaUrlBase && <p className="qr-code-btn-hint">{t('works.qrCode.pwaUrlMissing')}</p>}
              </>
            )}
          </>
        )}
        <button className="save-draft-btn" onClick={onSave} disabled={saving}>
          {saving
            ? t('common.loading')
            : statusByLanguage[currentLanguage] === 'published'
              ? t('exhibitions.publishWork')
              : t('exhibitions.saveDraft')}
        </button>
        <button className="cancel-link" onClick={onRequestClose}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
};
